import type { ExtractedData, Supplier, ValidationFormState } from '../types';

/**
 * Construit l'état initial du formulaire de validation.
 *
 * Précédence :
 *   1. `validated` (si la facture a déjà été validée et qu'on la rouvre)
 *   2. extracted + supplier match → on applique les règles par défaut du
 *      fournisseur (charge_type, payment_mode, banque, compte par ligne)
 *   3. extracted seul → on remplit ce qu'on peut
 *   4. tout vide → formulaire vierge
 */
export function buildInitialForm(args: {
  validated: ValidationFormState | null;
  extracted: ExtractedData | null;
  supplier: Supplier | null;
  /**
   * NDF Dominique : la description de ligne porte le nom commercial du
   * justificatif (SNCF, restaurant…) → on la conserve. Pour toutes les
   * autres factures, la description reste VIDE par défaut (Valérie la
   * remplit à la main si elle le juge pertinent).
   */
  isNdf?: boolean;
}): ValidationFormState {
  const { validated, extracted, supplier, isNdf = false } = args;

  if (validated) {
    // Compat : les anciennes factures validées n'ont pas zoho_vendor_id /
    // is_new_vendor / zoho_item_id. On les ajoute avec valeurs neutres si
    // absentes.
    return {
      ...validated,
      zoho_vendor_id: validated.zoho_vendor_id ?? null,
      zoho_vendor_name: validated.zoho_vendor_name ?? null,
      is_new_vendor: validated.is_new_vendor ?? false,
      zoho_item_id: validated.zoho_item_id ?? null,
      lines_already_in_eur: validated.lines_already_in_eur ?? false,
    };
  }

  const defaultAccount = supplier?.default_account ?? '';

  return {
    supplier_id: supplier?.id ?? null,
    // Pré-sélection du vendor Zoho si un alias a été résolu à l'extraction.
    zoho_vendor_id: extracted?.supplier.zoho_vendor_id ?? null,
    zoho_vendor_name: extracted?.supplier.zoho_vendor_name ?? null,
    is_new_vendor: false,
    zoho_item_id: null,
    lines_already_in_eur: false,
    invoice_number: extracted?.invoice.number ?? '',
    invoice_date: extracted?.invoice.date ?? '',
    due_date: extracted?.invoice.due_date ?? '',
    charge_type: supplier?.default_charge_type ?? '',
    pedagogic_allocation: supplier?.default_pedagogic_allocation ?? '',
    payment_mode: supplier?.default_payment_mode ?? extracted?.payment.mode ?? '',
    bank: supplier?.default_bank ?? '',
    lines: (extracted?.lines ?? []).map((l) => ({
      // Vide par défaut, sauf NDF Dominique (= nom commercial du justificatif).
      description: isNdf ? l.description : '',
      quantity: l.quantity,
      unit_price_ht: l.unit_price_ht,
      // Conservé sur la ligne : la conversion devise repart de cette valeur,
      // sans dépendre de la position de la ligne dans l'extraction.
      original_unit_price_ht: l.unit_price_ht,
      vat_rate: l.vat_rate ?? null,
      account: defaultAccount,
    })),
  };
}

/**
 * Niveau de confiance d'un champ donné.
 *
 * Simplifié pour ne pas tracker l'origine de chaque champ :
 *   - vide → low (rouge)
 *   - rempli + supplier identifié → high (vert)
 *   - rempli + supplier non identifié → medium (orange)
 *
 * Quand l'utilisateur sélectionne manuellement un supplier, tous les champs
 * dépendants repasseront en vert automatiquement.
 */
export type Confidence = 'high' | 'medium' | 'low';

export function fieldConfidence(value: unknown, supplierMatched: boolean): Confidence {
  if (value == null || value === '' || (Array.isArray(value) && value.length === 0)) {
    return 'low';
  }
  return supplierMatched ? 'high' : 'medium';
}
